import { TFunction } from 'i18next';
import { referenceDataSyncService } from '../services/referenceDataSyncService';
import { getLanguage } from '../helpers/fetcher';

export const getTypeOfEstablishmentOptions = async (
  t: TFunction,
  department?: string,
  municipality?: string
) => {
  const [establishmentTypes, establishments, locations] = await Promise.all([
    referenceDataSyncService.getEstablishmentTypes(),
    referenceDataSyncService.getEstablishments(),
    referenceDataSyncService.getLocations(),
  ]);

  const lang = getLanguage();

  // Map of all establishment types by key, id, and names
  const typesByKey = new Map<string, { label: string; value: string; id?: number }>();
  const typesById = new Map<number, { label: string; value: string; id?: number }>();

  establishmentTypes.forEach((estType: any) => {
    if (estType) {
      const label =
        lang === 'es'
          ? estType.nameEs || estType.nameEn || estType.key
          : estType.nameEn || estType.nameEs || estType.key;
      const value = estType.key || estType.nameEn || String(estType.id);
      const entry = { label, value, id: estType.id };
      if (estType.key) typesByKey.set(String(estType.key).toLowerCase(), entry);
      if (estType.id) typesById.set(Number(estType.id), entry);
      if (estType.nameEn) typesByKey.set(String(estType.nameEn).toLowerCase(), entry);
      if (estType.nameEs) typesByKey.set(String(estType.nameEs).toLowerCase(), entry);
    }
  });

  if (department || municipality) {
    const filteredEstablishments = establishments.filter((est: any) => {
      const location = locations.find(
        (loc: any) => Number(loc.id) === Number(est.locationId)
      );
      const estDept =
        location?.district || est.department || est.location?.district;
      const estMun =
        location?.city || est.municipality || est.location?.city;

      let matches = true;
      if (department) {
        matches =
          matches &&
          String(estDept).toLowerCase() === String(department).toLowerCase();
      }
      if (municipality) {
        matches =
          matches &&
          String(estMun).toLowerCase() === String(municipality).toLowerCase();
      }
      return matches;
    });

    const resultMap = new Map<string, { label: string; value: string; id?: number }>();
    filteredEstablishments.forEach((est: any) => {
      const estType = est.establishmentType;
      const key =
        estType?.key ||
        (typeof estType === 'string' ? estType : null) ||
        est.positionType;
      const typeId = estType?.id || est.establishmentTypeId;

      if (key && typesByKey.has(String(key).toLowerCase())) {
        const found = typesByKey.get(String(key).toLowerCase())!;
        resultMap.set(found.value, found);
      } else if (typeId && typesById.has(Number(typeId))) {
        const found = typesById.get(Number(typeId))!;
        resultMap.set(found.value, found);
      } else if (key) {
        const label =
          estType && typeof estType === 'object'
            ? lang === 'es'
              ? estType.nameEs || estType.nameEn || key
              : estType.nameEn || estType.nameEs || key
            : key;
        resultMap.set(String(key), { label, value: String(key), id: typeId });
      }
    });

    return Array.from(resultMap.values()).sort((a, b) =>
      (a.label || '').localeCompare(b.label || '')
    );
  }

  // If no department/municipality filter, return all establishment types
  const allTypes = Array.from(
    new Set(
      Array.from(typesById.values()).concat(Array.from(typesByKey.values()))
    )
  );
  const uniqueResult = Array.from(new Map(allTypes.map((t) => [t.value, t])).values());
  return uniqueResult.sort((a, b) => (a.label || '').localeCompare(b.label || ''));
};
